import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  useTable,
  useGlobalFilter,
  useSortBy,
  usePagination,
} from "react-table";
import apiClient from "../utils/apiClient";
import { SERVER_BASE } from "../config/server";
import Swal from "sweetalert2";
import moment from "moment";
import "bootstrap/dist/css/bootstrap.min.css";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faEdit,
  faEllipsisV,
  faPencil,
  faPlus,
  faTrash,
} from "@fortawesome/free-solid-svg-icons";

const GlobalFilter = ({ globalFilter, setGlobalFilter }) => {
  return (
    <input
      className="form-control"
      value={globalFilter || ""}
      onChange={(e) => setGlobalFilter(e.target.value)}
      placeholder="Search..."
    />
  );
};

const RecipeConsumptionList = ({ permissions }) => {
  const navigate = useNavigate();
  const token = localStorage.getItem("token");

  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);

  const fetchConsumptionList = async () => {
    try {
      setLoading(true);
      const res = await apiClient.get(`${SERVER_BASE}/getAllRecipeConsumptions`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setData(res.data || []);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    const confirm = await Swal.fire({
      title: "Are you sure?",
      text: "This will permanently delete the consumption record",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Yes, delete",
    });

    if (!confirm.isConfirmed) return;

    try {
      const response=await apiClient.delete(`${SERVER_BASE}/deleteRecipeConsumptions/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });

            Swal.fire({
              icon: "success",
              title: response?.data?.message,
              toast: true,
              timer: 1500,
              position: "top-end",
              showConfirmButton: false,
            });
      fetchConsumptionList();
    } catch (err) {
      console.error(err);
    }
  };

  const columns = useMemo(
    () => [
      {
        Header: "#",
        Cell: ({ row }) => row.index + 1,
        disableSortBy: true,
      },
      {
        Header: "Date",
        accessor: "date",
        Cell: ({ value }) => moment(value).format("DD MMM YYYY"),
      },
      {
        Header: "Transaction",
        accessor: "transaction",
      },
      {
        Header: "Recipe",
        accessor: "recipeName",
      },
      {
        Header: "Department",
        accessor: "department",
      },
      {
        Header: "Items Count",
        accessor: (row) => row.items?.length || 0,
        id: "itemsCount",
      },
      // {
      //   Header: "Actions",
      //   disableSortBy: true,
      //   Cell: ({ row }) => (
      //     <>
      //       <button
      //         className="btn btn-sm btn-warning me-2"
      //         onClick={() =>
      //           navigate("../RecipeConsumption", {
      //             state: { id: row.original.id },
      //           })
      //         }
      //       >
      //         <FontAwesomeIcon icon={faEdit} />
      //       </button>
      //       <button
      //         className="btn btn-sm btn-danger"
      //         onClick={() => handleDelete(row.original.id)}
      //       >
      //         <FontAwesomeIcon icon={faTrash} />
      //       </button>
      //     </>
      //   ),
      // },
      {
        Header: "#",
        accessor: "actions",
        Cell: ({ row }) => {
          const creator = row?.original?.creator;
          const updater = row?.original?.updater;

          const createdBy = creator
            ? [creator.firstName, creator.lastName].filter(Boolean).join(" ")
            : null;

          const updatedBy = updater
            ? [updater.firstName, updater.lastName].filter(Boolean).join(" ")
            : null;

          const createdAt = row?.original?.createdAt;
          const updatedAt = row?.original?.updatedAt;

          return (
            <div className="dropdown">
              <button
                className="btn btn-light"
                type="button"
                id={`dropdownMenuButton-${row.index}`}
                data-bs-toggle="dropdown"
                aria-expanded="false"
              >
                <FontAwesomeIcon icon={faEllipsisV} />
              </button>

              <ul
                className="dropdown-menu dropdown-menu-end"
                aria-labelledby={`dropdownMenuButton-${row.index}`}
              >
                {permissions?.edit && (
                  <li>
                    <button
                      className="dropdown-item"
                      onClick={() =>
                        navigate("../RecipeConsumption", {
                          state: { id: row.original.id },
                        })
                      }
                    >
                      <FontAwesomeIcon icon={faEdit} /> Edit
                    </button>
                  </li>
                )}

                {permissions?.delete && (
                  <li>
                    <button
                      className="dropdown-item text-danger"
                      onClick={() => handleDelete(row.original.id)}
                    >
                      <FontAwesomeIcon icon={faTrash} /> Delete
                    </button>
                  </li>
                )}
                {(createdBy || updatedBy) && (
                  <li>
                    <hr className="dropdown-divider" />
                  </li>
                )}

                {createdBy && (
                  <li className="dropdown-item">
                    <FontAwesomeIcon icon={faPlus} className="me-1" />
                    {createdBy}
                    {createdAt &&
                      ` (${moment(createdAt).format("DD-MMM-YY hh:mm A")})`}
                  </li>
                )}

                {updatedBy && (
                  <li className="dropdown-item">
                    <FontAwesomeIcon icon={faPencil} className="me-1" />
                    {updatedBy}
                    {updatedAt &&
                      ` (${moment(updatedAt).format("DD-MMM-YY hh:mm A")})`}
                  </li>
                )}
              </ul>
            </div>
          );
        },
      },
    ],
    [permissions]
  );

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    prepareRow,
    page,
    state,
    setGlobalFilter,
    setPageSize,
    nextPage,
    previousPage,
    canNextPage,
    canPreviousPage,
    pageOptions,
  } = useTable(
    {
      columns,
      data,
      initialState: {
        pageSize: 25,
      },
    },
    useGlobalFilter,
    useSortBy,
    usePagination
  );

  const { globalFilter, pageIndex, pageSize } = state;

  useEffect(() => {
    fetchConsumptionList();
  }, []);

  return (
    <div className="formcontainer mt-5">
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h4>Recipe Consumption</h4>
      </div>

      <div className="mb-3 d-flex gap-2">
        {permissions?.write && (
        <button
          className="btn btn-outline-success"
          onClick={() => navigate("../RecipeConsumption")}
          style={{ width: "15%" }}
        >
          Add New
        </button>
        )}

{permissions?.read && ( <>
        <GlobalFilter
          globalFilter={globalFilter}
          setGlobalFilter={setGlobalFilter}
          className="w-auto"
        />

        <select
          className="form-select w-auto"
          value={pageSize}
          onChange={(e) => setPageSize(Number(e.target.value))}
        >
          {[25, 50, 100, 200].map((size) => (
            <option key={size} value={size}>
              Show {size}
            </option>
          ))}
        </select>
        </>)}
      </div>
      {permissions?.read && ( <>
      <table className="table table-bordered table-sm" {...getTableProps()}>
        <thead className="table-light">
          {headerGroups.map((headerGroup) => (
            <tr {...headerGroup.getHeaderGroupProps()}>
              {headerGroup.headers.map((column) => (
                <th
                  {...column.getHeaderProps(column.getSortByToggleProps())}
                  style={{ cursor: column.canSort ? "pointer" : "default" }}
                >
                  {column.render("Header")}
                  {column.isSorted ? (column.isSortedDesc ? " 🔽" : " 🔼") : ""}
                </th>
              ))}
            </tr>
          ))}
        </thead>

        <tbody {...getTableBodyProps()}>
          {loading ? (
            <tr>
              <td colSpan="8" className="text-center">
                Loading...
              </td>
            </tr>
          ) : page.length === 0 ? (
            <tr>
              <td colSpan="8" className="text-center">
                No records found
              </td>
            </tr>
          ) : (
            page.map((row) => {
              prepareRow(row);
              return (
                <tr {...row.getRowProps()}>
                  {row.cells.map((cell) => (
                    <td className="align-content-center"{...cell.getCellProps()}>{cell.render("Cell")}</td>
                  ))}
                </tr>
              );
            })
          )}
        </tbody>
      </table>
      <div className="d-flex justify-content-between align-items-center mt-2">
        <button
          className="btn btn-sm btn-secondary me-2"
          onClick={previousPage}
          disabled={!canPreviousPage}
        >
          Previous
        </button>
        <span>
          Page <strong>{pageIndex + 1}</strong> of{" "}
          <strong>{pageOptions.length}</strong>
        </span>
        <button
          className="btn btn-sm btn-secondary"
          onClick={nextPage}
          disabled={!canNextPage}
        >
          Next
        </button>
      </div>
</>)}
    </div>
  );
};

export default RecipeConsumptionList;
